/**
=========================================================
* Trust Cargo - filter bar
=========================================================

* One search control that works on every list in the system.
*
* Rather than hand-writing which columns are searchable per page (and getting
* it wrong whenever a column is added), `filterRows` walks each row's own
* values. So on the orders page it matches shop code, customer name, goods
* type and container number without any of that being declared anywhere.
*/

import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import InputBase from "@mui/material/InputBase";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

/**
 * Case-insensitive match across every scalar value of a row, one level deep
 * into nested objects (customer.name, container.file_no ...).
 *
 * Splitting the query on spaces and requiring ALL terms lets someone type
 * "ahmad 604" to find Ahmad's rows in container 604 - the search people
 * actually attempt on these screens.
 */
export function filterRows(rows, query) {
  if (!Array.isArray(rows)) return [];

  const terms = String(query || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (terms.length === 0) return rows;

  const haystack = (row) => {
    if (row === null || row === undefined) return "";
    if (typeof row !== "object") return String(row);

    return Object.values(row)
      .map((v) => {
        if (v === null || v === undefined) return "";
        if (typeof v === "object") {
          // one level deep only - deeper is rarely meaningful and gets slow
          return Object.values(v)
            .filter((x) => x !== null && typeof x !== "object")
            .join(" ");
        }
        return String(v);
      })
      .join(" ");
  };

  return rows.filter((row) => {
    const text = haystack(row).toLowerCase();
    return terms.every((term) => text.includes(term));
  });
}

/**
 * One toolbar row per page: title, search, extra controls, record count and
 * the page's action buttons all live here.
 *
 * They used to be four stacked rows (navbar title, card header, filter chips,
 * search) which pushed every table below the fold. Anything that does not fit
 * wraps rather than forcing a new row on wide screens.
 */
function FilterBar({ value, onChange, count, total, placeholder, children, title, actions }) {
  const filtering = String(value || "").length > 0;

  return (
    <SuiBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5} px={3} py={2} width="100%">
      {title && (
        <SuiTypography variant="h6" sx={{ mr: 1, whiteSpace: "nowrap" }}>
          {title}
        </SuiTypography>
      )}

      <SuiBox
        display="flex"
        alignItems="center"
        gap={1}
        sx={({ palette, borders, functions: { rgba } }) => ({
          flex: "1 1 260px",
          maxWidth: 420,
          px: 1.5,
          py: 0.75,
          borderRadius: borders.borderRadius.md,
          backgroundColor: palette.grey[100],
          border: `1px solid ${rgba(palette.dark.main, filtering ? 0.18 : 0.08)}`,
          transition: "border-color 160ms ease, box-shadow 160ms ease",
          "&:focus-within": {
            borderColor: rgba(palette.primary.main, 0.55),
            boxShadow: `0 0 0 3px ${rgba(palette.primary.main, 0.14)}`,
          },
        })}
      >
        <Icon
          sx={({ palette, functions: { rgba } }) => ({
            color: filtering ? palette.primary.main : rgba(palette.dark.main, 0.4),
            fontSize: 20,
          })}
        >
          search
        </Icon>

        <InputBase
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          sx={({ palette, typography }) => ({
            flex: 1,
            fontSize: typography.size.sm,
            fontFamily: typography.fontFamily,
            color: palette.text.focus,
          })}
        />

        {filtering && (
          <Icon
            onClick={() => onChange("")}
            sx={({ palette, functions: { rgba } }) => ({
              cursor: "pointer",
              fontSize: 18,
              color: rgba(palette.dark.main, 0.45),
              "&:hover": { color: palette.error.main },
            })}
          >
            close
          </Icon>
        )}
      </SuiBox>

      {/* extra per-page controls (selects, date ranges) drop in here */}
      {children}

      {/* count and buttons pushed to the far end of the same row */}
      <SuiBox
        flex="1 1 auto"
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        gap={1.5}
        flexWrap="wrap"
      >
        <SuiTypography
          variant="caption"
          sx={({ palette, functions: { rgba } }) => ({
            color: rgba(palette.dark.main, 0.55),
            whiteSpace: "nowrap",
          })}
        >
          {filtering ? `${count} of ${total}` : `${total} records`}
        </SuiTypography>

        {actions}
      </SuiBox>
    </SuiBox>
  );
}

FilterBar.defaultProps = {
  placeholder: "Search...",
  count: 0,
  total: 0,
  children: null,
  title: null,
  actions: null,
};

FilterBar.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  count: PropTypes.number,
  total: PropTypes.number,
  placeholder: PropTypes.string,
  // filter chips / date pickers, rendered next to the search box
  children: PropTypes.node,
  // page name, shown at the start of the row
  title: PropTypes.node,
  // buttons, pushed to the end of the row
  actions: PropTypes.node,
};

export default FilterBar;
